/**
 * Level 94: the stop-motion neighbourhood.
 *
 * A model village. Everything here is the same nine tenths too small, painted in
 * flat saturated colours, and completely undamaged — this is the one level in
 * the game with no grime on it, and that is what makes it wrong. Nothing is
 * tipped over, nothing is stained, no variant is a broken version of another
 * variant. A suburb where the gate is shut and the washing is still out reads as
 * a place somebody made rather than a place somebody left.
 *
 * Three rules the rest of the catalogue does not have.
 *
 *  - **Barely tinted.** `propPalette` pulls every base a third of the way into
 *    the level's air so that scenery belongs to the room it stands in. Out here
 *    the air is daylight and the objects are painted models, so they are pulled
 *    a fifth as hard (`flat`). A hedge on this level is *green*, not
 *    green-under-sodium; that flatness is the whole look.
 *  - **Nothing has settled.** No `tilt`, no tumbled variants, no rust. Variety
 *    comes from three shapes of the same clean object.
 *  - **Some of it is taller than the ceiling.** See `skyward`.
 *
 * Every model here is centred on its own origin in XZ and sized to the
 * collision box in `world/gen/propTables.ts`, measured at the widest variant:
 *
 *   kind           box halfX / halfZ / height   widest model  |x|, |z|, top
 *   picket-fence   1.20 / 0.09 / 1.00           1.195, 0.075, 1.000
 *   mailbox        0.18 / 0.18 / 1.15           0.157, 0.170, 1.145
 *   lamp-post      0.16 / 0.16 / 3.40           0.155, 0.155, 3.390
 *   shrub          0.45 / 0.45 / 0.95           0.418, 0.408, 0.940
 *   garden-tree    0.32 / 0.32 / 4.20           0.180, 0.180, 4.200  (canopy over)
 *   swing-set      1.35 / 0.75 / 2.30           1.340, 0.740, 2.212
 *   parked-car     0.85 / 2.05 / 1.50           0.830, 1.935, 1.500
 *   water-tower    2.20 / 2.20 / 12.0           2.180, 2.180, 12.000
 *   laundry-line   1.60 / 0.08 / 2.00           1.575, 0.075, 1.920
 *
 * `garden-tree` is the one deliberate overhang: the trunk is inside its box and
 * the canopy is not, because a tree you cannot walk under is a pillar. Nothing
 * of the canopy comes below 2.1 m, so there is nothing to walk into.
 */

import * as THREE from 'three';
import type { LevelSpec } from '../core/types';
import {
  belong,
  box,
  cyl,
  makeShape,
  matte,
  merge,
  propPalette,
  strut,
  type Anchor,
  type PropLayer,
  type PropPalette,
  type PropShape,
} from './propkit';

/* ------------------------------------------------------------------ *
 * Local geometry helpers
 * ------------------------------------------------------------------ */

/** A capped cylinder lying along X: axles, mailbox bodies, top bars. */
function rodX(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  g.rotateZ(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/** A capped cylinder lying along Z. */
function rodZ(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1);
  g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/**
 * A low-poly blob. Every soft mass on this level — canopies, hedges, the crown
 * of a bonnet — is one of these, because a model village is made of shapes that
 * were rolled rather than cut.
 */
function blob(rx: number, ry: number, rz: number, x = 0, y = 0, z = 0): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, 7, 5);
  g.scale(rx, ry, rz);
  g.translate(x, y, z);
  return g;
}

/**
 * A coarser blob, for the parts of a car that are only rounding off a corner.
 * Half the triangles of `blob`, and at nine tenths scale nobody can tell the
 * difference between a six-sided fender and a fourteen-sided one.
 */
function pebble(rx: number, ry: number, rz: number, x = 0, y = 0, z = 0): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(1, 6, 4);
  g.scale(rx, ry, rz);
  g.translate(x, y, z);
  return g;
}

/** A box leaned about Z, for gate braces and anything on a diagonal. */
function zbox(w: number, h: number, d: number, x: number, y: number, z: number, roll: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  g.rotateZ(roll);
  g.translate(x, y, z);
  return g;
}

function layer(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer {
  return { material, variants };
}

/** Shorthand for a kind whose every variant is the same single layer. */
function solo(material: THREE.Material, variants: Array<THREE.BufferGeometry | null>): PropLayer[] {
  return [layer(material, variants)];
}

/**
 * Author a prop taller than the level thinks its ceiling is.
 *
 * `anchorProp` shrinks a prop until its top fits under `spec.ceilingHeight`,
 * which is correct for a filing cabinet in a 2.4 m room and ruinous here: Level
 * 94's ceiling height is *house* height, and the lanes have no roof over them
 * at all. Left alone, the 12 m water tower would render at 28% and stand three
 * metres tall inside a four-metre collision box.
 *
 * So these opt out of the clamp instead. They are drawn standing on their own
 * origin like everything else, which is what keeps `PropSpot.scale` scaling them
 * about the ground they stand on — a model hung below its origin to dodge the
 * clamp grows and shrinks about its *top*, so a 1.25-scale tree ends up with a
 * metre of trunk underground and a 0.85 one hovers over the lawn.
 */
function skyward(yawJitter = 0): Anchor {
  return { kind: 'floor', unroofed: true, yawJitter };
}

/* ------------------------------------------------------------------ *
 * Palette
 * ------------------------------------------------------------------ */

/**
 * Paint, not material. A fifth of the usual pull toward the level's air and no
 * loss of gain, so the colours stay flat and saturated the way a painted model
 * does. Anything nearer the ordinary pull comes out as weathered outdoor kit,
 * which is the one thing this level must never look like.
 */
function flat(hex: number, pal: PropPalette): THREE.Color {
  return belong(hex, pal, pal.pull * 0.2, 1.0);
}

/**
 * The level's paint box. Exported because `propsubhouse.ts` builds the houses
 * and the hills out of the same colours the gardens are painted in — a roof in
 * a red the mailboxes do not use is the one thing that would break a village
 * whose whole look is that everything came out of the same tin.
 */
export interface SuburbPalette {
  /** The one white the whole village is painted. */
  paint: THREE.Color;
  /** Post-box red. Also the roofs, in the mind of whoever built this. */
  red: THREE.Color;
  leaf: THREE.Color;
  leafDeep: THREE.Color;
  trunk: THREE.Color;
  /** Galvanised blue-grey: lamp posts, swing frames, the tower's steelwork. */
  steel: THREE.Color;
  rubber: THREE.Color;
  /** Nearly a hole. Whatever is behind a window is never modelled. */
  glass: THREE.Color;
  chrome: THREE.Color;
  bodyA: THREE.Color;
  bodyB: THREE.Color;
  bodyC: THREE.Color;
  cloth: THREE.Color;
  clothCool: THREE.Color;
  /**
   * The lawn, taken straight off the level's own floor colour rather than
   * chosen. Every house stands on a mound of this, and a mound in a green the
   * ground is not reads as a tarpaulin thrown over the garden.
   */
  turf: THREE.Color;
  /** Three roofs, so a row of houses is a row of houses. */
  tileRed: THREE.Color;
  tileSlate: THREE.Color;
  tileMoss: THREE.Color;
  /** Chimney stacks, and the only warm brown above ground level. */
  brick: THREE.Color;
}

/** Level 94's colours, derived once per level from its own air and floor. */
export function suburb(spec: LevelSpec): SuburbPalette {
  const pal = propPalette(spec);
  return {
    paint: flat(0xf6f3ea, pal),
    red: flat(0xcf4437, pal),
    leaf: flat(0x57a844, pal),
    leafDeep: flat(0x3f8437, pal),
    trunk: flat(0x8b5b3b, pal),
    steel: flat(0x5b6b76, pal),
    rubber: flat(0x2e3234, pal),
    glass: belong(0x22333c, pal, pal.pull * 0.4, 0.95),
    chrome: flat(0xd2d7db, pal),
    bodyA: flat(0xe3d6b1, pal),
    bodyB: flat(0x3d6b9e, pal),
    bodyC: flat(0x8e3b34, pal),
    cloth: flat(0xf2efe3, pal),
    clothCool: flat(0xa6c3dd, pal),
    // Not pulled toward the air at all: this has to be the floor, and the floor
    // material is built from `palette.carpet` unchanged.
    turf: new THREE.Color(spec.palette.carpet),
    tileRed: flat(0xb1463a, pal),
    tileSlate: flat(0x6a7580, pal),
    tileMoss: flat(0x5d7a52, pal),
    brick: flat(0x9c6a4e, pal),
  };
}

/* ================================================================== *
 * The landmark
 * ================================================================== */

/**
 * The water tower. The only thing on Level 94 taller than a house, standing on
 * open ground in the middle of the map, and the reason this is the one level
 * where being lost is a choice: from anywhere the daylight reaches, the tank
 * tells you which way the middle is.
 *
 * So the silhouette is doing all the work and is drawn for reading at range —
 * four splayed legs with two rings of bracing, a catwalk, a barrel tank and a
 * cone roof. One variant, because there is one of it.
 */
export function buildWaterTower(spec: LevelSpec): PropShape {
  const pal = suburb(spec);
  const H = 12;
  /** Underside of the tank, and the height of the catwalk. */
  const DECK = 7.1;
  const TANK = 3.3;
  const R = 1.9;
  /** Foot and shoulder half-spacing of a leg, so it splays. */
  const FOOT = 1.5;
  const SHOULDER = 0.92;

  /** Where a leg is at height `y`, for the bracing to meet it. */
  const at = (sx: number, sz: number, y: number): [number, number, number] => {
    const k = FOOT + (SHOULDER - FOOT) * (y / DECK);
    return [sx * k, y, sz * k];
  };

  const corners: ReadonlyArray<readonly [number, number]> = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ];

  const steel: THREE.BufferGeometry[] = [];
  for (const [sx, sz] of corners) {
    steel.push(strut(0.085, sx * FOOT, 0.06, sz * FOOT, sx * SHOULDER, DECK, sz * SHOULDER, 5));
    steel.push(box(0.32, 0.12, 0.32, sx * FOOT, 0.06, sz * FOOT));
  }
  // Two rings of horizontal bracing and one diagonal per face. Enough to read
  // as a structure from two hundred metres; not enough to cost anything.
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % corners.length]!;
    for (const y of [2.5, 5.0]) {
      const [ax, ay, az] = at(a[0], a[1], y);
      const [bx, by, bz] = at(b[0], b[1], y);
      steel.push(strut(0.045, ax, ay, az, bx, by, bz, 4));
    }
    const [dx0, dy0, dz0] = at(a[0], a[1], 2.5);
    const [dx1, dy1, dz1] = at(b[0], b[1], 5.0);
    steel.push(strut(0.035, dx0, dy0, dz0, dx1, dy1, dz1, 4));
  }

  // Catwalk: a deck, a toe board and a rail. The rail is the widest thing on
  // the tower and is inside the collision box by two centimetres.
  steel.push(
    cyl(2.12, 2.12, 0.07, 12, 0, DECK + 0.035, 0),
    cyl(2.15, 2.15, 0.12, 12, 0, DECK + 0.13, 0, true),
    cyl(2.18, 2.18, 0.05, 12, 0, DECK + 0.92, 0, true)
  );
  for (const [sx, sz] of corners) {
    steel.push(box(0.05, 0.9, 0.05, sx * 1.5, DECK + 0.5, sz * 1.5));
  }

  // The ladder nobody has climbed, and the downpipe to a main that is not there.
  for (const x of [-0.3, 0.3]) steel.push(strut(0.035, x, 0.9, -1.12, x, DECK + 0.35, -1.12, 4));
  for (let y = 1.15; y < DECK; y += 0.55) steel.push(box(0.62, 0.045, 0.045, 0, y, -1.12));
  steel.push(strut(0.06, 1.32, DECK - 0.1, 0.02, 1.32, 0.3, 0.02, 5));

  const tank: THREE.BufferGeometry[] = [
    cyl(R, R, TANK, 12, 0, DECK + 0.07 + TANK / 2, 0),
    cyl(R + 0.05, R + 0.05, 0.1, 12, 0, DECK + 0.75, 0, true),
    cyl(R + 0.05, R + 0.05, 0.1, 12, 0, DECK + 2.65, 0, true),
    // Cone roof, vent and finial. The top of the finial is exactly 12 m.
    cyl(0.14, R + 0.12, 0.92, 12, 0, DECK + TANK + 0.53, 0),
    cyl(0.17, 0.17, 0.22, 8, 0, DECK + TANK + 1.1, 0),
    cyl(0.045, 0.045, 0.26, 6, 0, DECK + TANK + 1.34, 0),
    blob(0.1, 0.1, 0.1, 0, DECK + TANK + 1.5, 0),
  ];

  const steelGeo = merge(steel);
  const tankGeo = merge(tank);
  const anchor = skyward(0.25);

  return makeShape(spec, anchor, [
    layer(matte(pal.steel), [steelGeo]),
    layer(matte(pal.paint), [tankGeo]),
  ]);
}

/* ================================================================== *
 * Planting
 * ================================================================== */

/**
 * A tree the way a model railway makes one: a straight trunk and two or three
 * soft masses on top of it. No branch structure, because at nine tenths scale
 * and this light level a branch is four triangles of noise.
 *
 * The canopy is wider than the collision box on purpose — the box is the trunk,
 * and the leaves start at 2.1 m so there is nothing to walk into.
 */
export function buildGardenTree(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const round = merge([
    blob(1.0, 0.95, 1.0, 0, 3.25, 0),
    blob(0.6, 0.5, 0.6, -0.62, 2.85, 0.1),
    blob(0.54, 0.45, 0.54, 0.58, 2.95, -0.14),
  ]);
  // Nothing in any canopy comes below 2.2 m, so the smallest tree the placer can
  // draw still has 1.87 m of air under it and there is nothing to walk into.
  const tall = merge([blob(0.78, 1.0, 0.78, 0, 3.2, 0), blob(0.48, 0.42, 0.48, 0.36, 2.74, 0.22)]);
  const wide = merge([
    blob(1.14, 0.72, 1.14, 0, 3.05, 0),
    blob(0.68, 0.48, 0.68, -0.52, 2.76, -0.18),
    blob(0.6, 0.44, 0.6, 0.56, 2.78, 0.2),
  ]);

  const trunkRound = merge([cyl(0.1, 0.17, 2.6, 6, 0, 1.3, 0), blob(0.18, 0.09, 0.18, 0, 0.09, 0)]);
  const trunkTall = merge([cyl(0.09, 0.15, 2.9, 6, 0, 1.45, 0), blob(0.16, 0.08, 0.16, 0, 0.08, 0)]);
  const trunkWide = merge([cyl(0.11, 0.18, 2.2, 6, 0, 1.1, 0), blob(0.18, 0.09, 0.18, 0, 0.09, 0)]);

  const anchor = skyward(Math.PI);

  return makeShape(spec, anchor, [
    layer(matte(pal.trunk), [trunkRound, trunkTall, trunkWide]),
    // Two greens across the variants, so a row of trees is not one object
    // repeated. Both layers exist on every instance; the unused one is hidden.
    layer(matte(pal.leaf), [round, null, wide]),
    layer(matte(pal.leafDeep), [null, tall, null]),
  ]);
}

/** A clipped hedge ball. The most numerous thing on the level, so: one layer. */
export function buildShrub(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const dome = merge([blob(0.44, 0.47, 0.44, 0, 0.47, 0)]);
  const pair = merge([blob(0.34, 0.38, 0.34, -0.09, 0.38, 0.06), blob(0.3, 0.32, 0.3, 0.14, 0.55, -0.08)]);
  const low = merge([blob(0.43, 0.3, 0.43, 0, 0.3, 0), blob(0.26, 0.24, 0.26, -0.1, 0.55, 0.04)]);

  return makeShape(spec, { kind: 'floor', yawJitter: Math.PI }, solo(matte(pal.leaf), [dome, pair, low]));
}

/* ================================================================== *
 * The plot boundary
 * ================================================================== */

/**
 * A run of picket fence, 2.4 m of it, with a post at each end so that two of
 * them laid end to end read as one fence. The gate variant is shut. Nobody has
 * been through it, and a gate standing open would be the level admitting that
 * somebody once lived here.
 */
export function buildPicketFence(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  /** One picket: a board with a point on it. */
  const picket = (x: number, top: number): THREE.BufferGeometry[] => [
    box(0.085, top - 0.16, 0.032, x, (top + 0.08) / 2 - 0.04, 0),
    cyl(0, 0.055, 0.08, 4, x, top - 0.04, 0),
  ];
  const post = (x: number, top: number): THREE.BufferGeometry[] => [
    box(0.11, top - 0.09, 0.11, x, (top - 0.09) / 2, 0),
    cyl(0, 0.075, 0.09, 4, x, top - 0.045, 0),
  ];
  const rails = (span: number, z = 0): THREE.BufferGeometry[] => [
    box(span, 0.07, 0.045, 0, 0.31, z),
    box(span, 0.07, 0.045, 0, 0.66, z),
  ];

  // One panel per grid cell, so a panel has to be a grid cell long. At 2.4 m on
  // Level 94's 3.2 m grid, a boundary of these came out as a row of fences with
  // 80 cm of nothing between them — twice the player's width, so the fence was
  // scenery rather than a boundary. `pipe-run` solves the same problem the same
  // way. 3.26 m spans the cell at every scale the placer draws (0.95 leaves a
  // 10 cm joint, which reads as a joint).
  const HALF = 1.55;
  const SPAN = HALF * 2;

  const plain: THREE.BufferGeometry[] = [...post(-HALF, 1.0), ...post(HALF, 1.0), ...rails(SPAN)];
  for (let i = 0; i < 13; i++) plain.push(...picket(-1.44 + i * 0.24, 0.94));

  // The same fence with a gate hung in the middle of it.
  const gated: THREE.BufferGeometry[] = [
    ...post(-HALF, 1.0),
    ...post(HALF, 1.0),
    ...post(-0.5, 1.0),
    ...post(0.5, 1.0),
    box(1.05, 0.07, 0.045, -1.025, 0.31, 0),
    box(1.05, 0.07, 0.045, -1.025, 0.66, 0),
    box(1.05, 0.07, 0.045, 1.025, 0.31, 0),
    box(1.05, 0.07, 0.045, 1.025, 0.66, 0),
    // The leaf: two rails, a brace, and its own shorter pickets.
    box(0.9, 0.06, 0.04, 0, 0.34, 0.035),
    box(0.9, 0.06, 0.04, 0, 0.68, 0.035),
    zbox(0.94, 0.05, 0.03, 0, 0.51, 0.035, 0.38),
  ];
  for (let i = 0; i < 5; i++) gated.push(...picket(-1.44 + i * 0.2, 0.9));
  for (let i = 0; i < 5; i++) gated.push(...picket(0.64 + i * 0.2, 0.9));
  for (let i = 0; i < 4; i++) gated.push(...picket(-0.33 + i * 0.22, 0.86));

  // A lower run, for the front of a garden rather than the side of one.
  const low: THREE.BufferGeometry[] = [...post(-HALF, 0.78), ...post(HALF, 0.78), ...rails(SPAN)];
  for (let i = 0; i < 15; i++) low.push(...picket(-1.4 + i * 0.2, 0.72));

  // No yaw wobble at all: this is 2.4 m of thin panel and its collision box does
  // not turn with the model. A tenth of a radian swings an end 12 cm clear of it.
  return makeShape(
    spec,
    { kind: 'floor' },
    solo(matte(pal.paint), [merge(plain), merge(gated), merge(low)])
  );
}

/** A mailbox on a post. Shut, in all three variants. */
export function buildMailbox(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const post = (h: number) => box(0.085, h, 0.085, 0, h / 2, 0);
  const body = (y: number): THREE.BufferGeometry[] => [
    box(0.25, 0.14, 0.3, 0, y - 0.07, -0.02),
    rodZ(0.125, 0.3, 8, 0, y, -0.02),
  ];
  const door = (y: number) => box(0.22, 0.19, 0.02, 0, y - 0.03, -0.16);
  /** The flag, up or folded back along the body. */
  const flag = (y: number, up: boolean): THREE.BufferGeometry => {
    const g = box(0.025, 0.16, 0.03, 0, 0.08, 0);
    if (!up) g.rotateX(1.5);
    g.translate(0.145, y - 0.02, 0.0);
    return g;
  };

  const down = { paint: merge([post(0.92), flag(0.9, false)]), red: merge([...body(0.99), door(0.99)]) };
  const up = { paint: merge([post(0.92), flag(0.9, true)]), red: merge([...body(0.99), door(0.99)]) };
  const tallOne = { paint: merge([post(0.96), flag(0.94, false)]), red: merge([...body(1.02), door(1.02)]) };

  // A 36 cm box with nothing to spare, so no render-only wobble. What jitter
  // this level gets lives in `spot.yaw`, where the collision box can see it.
  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.paint), [down.paint, up.paint, tallOne.paint]),
    layer(matte(pal.red), [down.red, up.red, tallOne.red]),
  ]);
}

/**
 * A street lamp. One variant is lit, in daylight, which is the most ordinary
 * wrong thing on the level: nobody turned it off because there is nobody.
 */
export function buildLampPost(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const column = (): THREE.BufferGeometry[] => [
    cyl(0.13, 0.155, 0.24, 8, 0, 0.12, 0),
    cyl(0.05, 0.075, 2.62, 6, 0, 1.55, 0),
    cyl(0.09, 0.105, 0.1, 8, 0, 2.91, 0),
  ];
  const lantern = (): THREE.BufferGeometry[] => [
    cyl(0.15, 0.15, 0.04, 4, 0, 2.98, 0),
    cyl(0.03, 0.155, 0.14, 4, 0, 3.32, 0),
  ];
  const shade = (): THREE.BufferGeometry[] => [cyl(0.15, 0.15, 0.04, 4, 0, 2.98, 0)];

  const square = () => cyl(0.1, 0.135, 0.28, 4, 0, 3.14, 0);
  const bowl = () => blob(0.16, 0.13, 0.16, 0, 3.12, 0);

  // The lit and the dark lantern are the same ironwork, so they are the same
  // buffer: `makeShape` frees its geometry through a Set and does not mind a
  // variant appearing twice.
  const lanternColumn = merge([...column(), ...lantern()]);
  const bowlColumn = merge([...column(), ...shade()]);
  const litGlass = square();
  const darkGlass = square();
  const bowlGlass = bowl();

  const anchor = skyward(0.4);

  // Self-illuminated only. The light rig owns illumination; this has to look on,
  // not light the lawn.
  const glow = new THREE.MeshLambertMaterial({
    color: pal.paint,
    emissive: spec.palette.lightTint,
    emissiveIntensity: 0.55,
  });

  return makeShape(spec, anchor, [
    layer(matte(pal.steel), [lanternColumn, lanternColumn, bowlColumn]),
    layer(glow, [litGlass, null, null]),
    layer(matte(pal.glass, 0.02), [null, darkGlass, bowlGlass]),
  ]);
}

/* ================================================================== *
 * The garden
 * ================================================================== */

/** A swing set, hanging perfectly still. */
export function buildSwingSet(spec: LevelSpec): PropShape {
  const pal = suburb(spec);
  const BAR = 2.16;

  const frame = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [strut(0.06, -1.24, BAR, 0, 1.24, BAR, 0, 6)];
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(strut(0.05, sx * 1.26, 0.05, sz * 0.66, sx * 1.06, BAR - 0.02, 0, 5));
        parts.push(box(0.16, 0.1, 0.16, sx * 1.26, 0.05, sz * 0.66));
      }
    }
    return parts;
  };
  /** A swing: two chains and a seat, at rest. */
  const swing = (x: number, seatY: number): THREE.BufferGeometry[] => [
    strut(0.014, x - 0.17, BAR - 0.03, 0, x - 0.17, seatY, 0.02, 4),
    strut(0.014, x + 0.17, BAR - 0.03, 0, x + 0.17, seatY, 0.02, 4),
  ];
  const seat = (x: number, y: number) => box(0.44, 0.045, 0.17, x, y, 0.02);
  const bucket = (x: number, y: number): THREE.BufferGeometry[] => [
    box(0.34, 0.16, 0.26, x, y + 0.06, 0.02),
    box(0.34, 0.045, 0.26, x, y - 0.03, 0.02),
  ];

  const pairFrame = merge([...frame(), ...swing(-0.55, 0.48), ...swing(0.55, 0.48)]);
  const pairSeats = merge([seat(-0.55, 0.48), seat(0.55, 0.48)]);

  const trioFrame = merge([...frame(), ...swing(-0.78, 0.5), ...swing(0.78, 0.5), ...swing(0, 0.5)]);
  const trioSeats = merge([seat(-0.78, 0.5), seat(0.78, 0.5), seat(0, 0.5)]);

  const mixedFrame = merge([...frame(), ...swing(-0.55, 0.46), ...swing(0.58, 0.62)]);
  const mixedSeats = merge([seat(-0.55, 0.46), ...bucket(0.58, 0.62)]);

  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.steel), [pairFrame, trioFrame, mixedFrame]),
    layer(matte(pal.red), [pairSeats, trioSeats, mixedSeats]),
  ]);
}

/**
 * Washing on a line between two posts, in a garden nobody is in. The line sags
 * in three segments — a straight line between two posts is the one detail that
 * would make this read as a barrier rather than as somebody's Tuesday.
 */
export function buildLaundryLine(spec: LevelSpec): PropShape {
  const pal = suburb(spec);
  const TOP = 1.84;

  const posts = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1]) {
      parts.push(box(0.1, 1.86, 0.1, sx * 1.5, 0.93, 0), box(0.15, 0.06, 0.15, sx * 1.5, 1.89, 0));
    }
    // 18 mm of rope rather than 12: at four triangles a section, a thinner line
    // is a row of posts holding up nothing from more than a few metres away.
    parts.push(
      strut(0.018, -1.5, TOP, 0, -0.55, TOP - 0.1, 0, 4),
      strut(0.018, -0.55, TOP - 0.1, 0, 0.55, TOP - 0.1, 0, 4),
      strut(0.018, 0.55, TOP - 0.1, 0, 1.5, TOP, 0, 4)
    );
    return parts;
  };

  /** A hanging rectangle of cloth, with a fold so it is not a single plane. */
  const hung = (w: number, h: number, x: number, z = 0): THREE.BufferGeometry[] => [
    box(w, h, 0.045, x, TOP - 0.12 - h / 2, z),
    box(w * 0.55, h * 0.7, 0.045, x + w * 0.12, TOP - 0.14 - (h * 0.7) / 2, z + 0.022),
  ];
  const shirt = (x: number): THREE.BufferGeometry[] => [
    box(0.36, 0.44, 0.045, x, TOP - 0.36, 0),
    box(0.62, 0.14, 0.045, x, TOP - 0.2, 0),
  ];

  // Every variant carries something coloured. White sheets on white posts in
  // front of a white house under a white sky is a washing line nobody can see:
  // it came out as two thin sticks with a blank rectangle floating between them.
  // One red towel per line is what makes it read as washing from down the lane.
  const sheetsPosts = merge(posts());
  const sheetsPale = merge(hung(0.92, 0.8, -0.55));
  const sheetsCool = merge([...shirt(0.42), ...hung(0.34, 0.5, 0.98)]);
  const sheetsWarm = merge(hung(0.3, 0.62, 0.02));

  const onePosts = merge(posts());
  const onePale = merge(hung(1.4, 0.92, -0.24));
  const oneCool = merge(hung(0.36, 0.44, 0.86));
  const oneWarm = merge(hung(0.28, 0.5, 1.18));

  const towelPosts = merge(posts());
  const towelPale = merge(hung(0.38, 0.52, -0.86));
  const towelCool = merge([...hung(0.4, 0.58, -0.06), ...hung(0.3, 0.4, 1.1)]);
  const towelWarm = merge([...hung(0.36, 0.46, 0.72), ...hung(0.26, 0.36, -1.16)]);

  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.paint), [sheetsPosts, onePosts, towelPosts]),
    layer(matte(pal.cloth), [sheetsPale, onePale, towelPale]),
    layer(matte(pal.clothCool), [sheetsCool, oneCool, towelCool]),
    layer(matte(pal.red), [sheetsWarm, oneWarm, towelWarm]),
  ]);
}

/* ================================================================== *
 * The kerb
 * ================================================================== */

/**
 * A car at the kerb: rounded, upright, running boards, headlamps on stalks. No
 * badge, no plate, no wear, and three body colours across the variants because
 * a street of identical cars is a car park.
 *
 * Front is local -Z, which is the way the placer squares free-standing props to
 * the building, so a car parked along a lane lies along the lane.
 */
export function buildParkedCar(spec: LevelSpec): PropShape {
  const pal = suburb(spec);

  const running = (): THREE.BufferGeometry[] => [
    box(1.5, 0.46, 3.5, 0, 0.56, 0),
    box(0.16, 0.06, 1.3, -0.75, 0.4, 0.1),
    box(0.16, 0.06, 1.3, 0.75, 0.4, 0.1),
    pebble(0.2, 0.13, 0.52, -0.64, 0.66, -1.18),
    pebble(0.2, 0.13, 0.52, 0.64, 0.66, -1.18),
    pebble(0.18, 0.12, 0.46, -0.64, 0.66, 1.18),
    pebble(0.18, 0.12, 0.46, 0.64, 0.66, 1.18),
  ];
  const nose = (): THREE.BufferGeometry[] => [
    box(1.06, 0.3, 1.25, 0, 0.94, -1.12),
    pebble(0.53, 0.15, 0.63, 0, 1.06, -1.12),
  ];
  const wheels = (): THREE.BufferGeometry[] => {
    const parts: THREE.BufferGeometry[] = [];
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) parts.push(rodX(0.33, 0.16, 8, sx * 0.66, 0.33, sz * 1.18));
    }
    return parts;
  };
  const brightwork = (): THREE.BufferGeometry[] => [
    box(0.66, 0.36, 0.12, 0, 0.96, -1.79),
    pebble(0.12, 0.12, 0.12, -0.42, 1.07, -1.72),
    pebble(0.12, 0.12, 0.12, 0.42, 1.07, -1.72),
    box(1.1, 0.07, 0.07, 0, 0.62, -1.88),
    box(1.05, 0.07, 0.07, 0, 0.62, 1.9),
    rodX(0.11, 0.19, 6, -0.66, 0.33, -1.18),
    rodX(0.11, 0.19, 6, 0.66, 0.33, -1.18),
    rodX(0.11, 0.19, 6, -0.66, 0.33, 1.18),
    rodX(0.11, 0.19, 6, 0.66, 0.33, 1.18),
  ];

  // Saloon: a cabin with a boot behind it.
  const saloon = merge([
    ...running(),
    ...nose(),
    box(1.38, 0.5, 1.55, 0, 1.06, 0.35),
    pebble(0.69, 0.13, 0.78, 0, 1.35, 0.35),
    box(1.2, 0.34, 0.62, 0, 0.96, 1.55),
    pebble(0.6, 0.16, 0.31, 0, 1.1, 1.55),
  ]);
  const saloonGlass = merge([box(1.4, 0.26, 1.57, 0, 1.16, 0.35)]);

  // Van: the same chassis with a box on the back of it.
  const van = merge([...running(), ...nose(), box(1.44, 0.72, 2.2, 0, 1.14, 0.5)]);
  const vanGlass = merge([box(1.46, 0.24, 0.5, 0, 1.28, -0.42)]);

  // Pickup: a cab and an open bed.
  const pickup = merge([
    ...running(),
    ...nose(),
    box(1.38, 0.52, 1.0, 0, 1.07, -0.15),
    pebble(0.69, 0.13, 0.5, 0, 1.36, -0.15),
    box(1.34, 0.26, 1.5, 0, 0.92, 0.98),
    box(0.08, 0.2, 1.5, -0.66, 1.13, 0.98),
    box(0.08, 0.2, 1.5, 0.66, 1.13, 0.98),
    box(1.34, 0.2, 0.08, 0, 1.13, 1.7),
  ]);
  const pickupGlass = merge([box(1.4, 0.28, 1.02, 0, 1.18, -0.15)]);

  // Three bodies on one chassis, so the wheels and the brightwork are one buffer
  // shared by all three variants rather than three copies of the same thing.
  const tyres = merge(wheels());
  const trim = merge(brightwork());

  return makeShape(spec, { kind: 'floor' }, [
    layer(matte(pal.bodyA), [saloon, null, null]),
    layer(matte(pal.bodyB), [null, van, null]),
    layer(matte(pal.bodyC), [null, null, pickup]),
    layer(matte(pal.rubber), [tyres, tyres, tyres]),
    layer(matte(pal.glass, 0.02), [saloonGlass, vanGlass, pickupGlass]),
    layer(matte(pal.chrome), [trim, trim, trim]),
  ]);
}
